// See Issue #167
import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from '../../services/api';
import { WaitlistAnalyticsResponse } from '../../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { extractApiError } from '../../utils/errorUtils'; // Issue #964: Standardized error handling
import Papa from 'papaparse'; // Issue #826: CSV export
import { RefreshCw, FileText, FileSpreadsheet } from 'lucide-react'; // Issue #824, #826: Icons

/**
 * Admin Analytics Dashboard Component (Issue #630, #844)
 *
 * Displays comprehensive waitlist analytics including:
 * - Waitlist performance metrics
 * - Conversion funnel visualization
 * - User behavior patterns
 * - Real-time statistics
 * - Manual refresh button (#824)
 * - CSV/PDF data export (#826)
 * - ARIA labels for accessibility (#825)
 */
const AdminAnalyticsDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<WaitlistAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null); // Issue #844: Separate error state for exports
  const [refreshing, setRefreshing] = useState(false); // Issue #824: Track manual refresh state
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null); // Issue #824: Track last refresh time
  const [isExporting, setIsExporting] = useState(false); // Issue #844: Track export state to prevent duplicate downloads (UI state)
  const isExportingRef = useRef(false); // Issue #844: Synchronous ref guard to prevent race conditions in rapid clicks
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default: last 30 days
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  const fetchAnalytics = async (isManualRefresh = false) => {
    try {
      // Issue #824: Set refreshing state for manual refreshes
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Validate date range on frontend
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        setError('Start date must be before or equal to end date');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const data = await adminApi.getWaitlistAnalytics({
        start_date: startDate,
        end_date: endDate
      });
      setAnalytics(data);
      setLastRefreshed(new Date()); // Issue #824: Track refresh time
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Issue #824: Manual refresh handler
  const handleManualRefresh = () => {
    fetchAnalytics(true);
  };

  // Issue #844: Sanitize CSV values to prevent formula injection
  const sanitizeCSVValue = (value: string | number | null | undefined): string | number => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return value;

    const stringValue = String(value);
    // Trim leading/trailing whitespace to prevent bypass via " =formula"
    const trimmedValue = stringValue.trim();

    // Check if trimmed value starts with dangerous characters that could be interpreted as formulas
    const dangerousChars = ['=', '+', '-', '@', '\t', '\r'];
    if (dangerousChars.some(char => trimmedValue.startsWith(char))) {
      // Prefix original value (with whitespace) with single quote to treat as literal text
      return `'${stringValue}`;
    }
    return stringValue;
  };

  // Issue #826: CSV export handler
  const handleExportCSV = () => {
    if (!analytics) return;
    // Issue #844: Prevent duplicate exports using synchronous ref guard
    if (isExportingRef.current) return;

    try {
      isExportingRef.current = true; // Set synchronously to block rapid clicks
      setIsExporting(true); // Update UI state
      setExportError(null); // Clear any previous export errors

      // Issue #844: Apply sanitization to ALL string values to future-proof against formula injection
      const csvData = [
        // Summary metrics
        [sanitizeCSVValue('Waitlist Analytics Summary')],
        [sanitizeCSVValue('Date Range'), sanitizeCSVValue(`${startDate} to ${endDate}`)],
        [sanitizeCSVValue('Generated'), sanitizeCSVValue(new Date().toLocaleString())],
        [],
        [sanitizeCSVValue('Real-time Statistics')],
        [sanitizeCSVValue('Active Waitlist Entries'), analytics.real_time_stats.active_waitlist_entries],
        [sanitizeCSVValue('Pending Slot Offers'), analytics.real_time_stats.pending_slot_offers],
        [sanitizeCSVValue('Recent Accepts (24h)'), analytics.real_time_stats.recent_accepts_24h],
        [sanitizeCSVValue('Recent Declines (24h)'), analytics.real_time_stats.recent_declines_24h],
        [],
        [sanitizeCSVValue('Waitlist Performance')],
        [sanitizeCSVValue('Total Joins'), analytics.waitlist_performance.total_joins],
        [sanitizeCSVValue('Average Position When Offered'), sanitizeCSVValue(analytics.waitlist_performance.average_position_when_offered?.toFixed(1) || 'N/A')],
        [sanitizeCSVValue('Average Time on Waitlist (days)'), sanitizeCSVValue(analytics.waitlist_performance.average_time_on_waitlist_days?.toFixed(1) || 'N/A')],
        [],
        [sanitizeCSVValue('Conversion Rates')],
        [sanitizeCSVValue('Notification Rate (%)'), analytics.conversion_funnel.notification_rate],
        [sanitizeCSVValue('Acceptance Rate (%)'), analytics.conversion_funnel.acceptance_rate],
        [sanitizeCSVValue('Overall Conversion Rate (%)'), analytics.conversion_funnel.overall_conversion_rate],
        [],
        [sanitizeCSVValue('Status Breakdown')],
        [sanitizeCSVValue('Status'), sanitizeCSVValue('Count')],
        // Sanitize status names to prevent formula injection
        ...Object.entries(analytics.waitlist_performance.status_breakdown).map(([status, count]) => [
          sanitizeCSVValue(status),
          count
        ]),
        [],
        [sanitizeCSVValue('Most Popular Fairs')],
        [sanitizeCSVValue('Fair Name'), sanitizeCSVValue('Joins Count')],
        // Sanitize fair names to prevent formula injection
        ...analytics.user_behavior.most_popular_fairs.map((fair) => [
          sanitizeCSVValue(fair.fair_name),
          fair.joins_count
        ])
      ];

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `waitlist-analytics-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Issue #844: Clean up object URL to avoid memory leaks
    } catch (error) {
      console.error('CSV export failed:', error);
      setExportError('Failed to export CSV. Please try again.');
    } finally {
      isExportingRef.current = false; // Reset ref guard
      setIsExporting(false); // Reset UI state
    }
  };

  // Issue #826: PDF export handler with lazy loading to reduce initial bundle
  const handleExportPDF = async () => {
    if (!analytics) return;
    // Issue #844: Prevent duplicate exports using synchronous ref guard
    if (isExportingRef.current) return;

    try {
      isExportingRef.current = true; // Set synchronously to block rapid clicks
      setIsExporting(true); // Update UI state
      setExportError(null); // Clear any previous export errors

      // Lazy load jspdf libraries only when needed
      const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
      ]);

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();

      // Title
      doc.setFontSize(18);
      doc.text('Waitlist Analytics Dashboard', pageWidth / 2, 15, { align: 'center' });

      // Date range
      doc.setFontSize(10);
      doc.text(`Date Range: ${startDate} to ${endDate}`, pageWidth / 2, 22, { align: 'center' });
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 27, { align: 'center' });

      let yPos = 35;

      // Real-time Statistics
      doc.setFontSize(14);
      doc.text('Real-time Statistics', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: [
          ['Active Waitlist Entries', analytics.real_time_stats.active_waitlist_entries.toString()],
          ['Pending Slot Offers', analytics.real_time_stats.pending_slot_offers.toString()],
          ['Recent Accepts (24h)', analytics.real_time_stats.recent_accepts_24h.toString()],
          ['Recent Declines (24h)', analytics.real_time_stats.recent_declines_24h.toString()]
        ],
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185] }
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yPos = (doc as any).lastAutoTable.finalY + 10;

      // Waitlist Performance
      doc.setFontSize(14);
      doc.text('Waitlist Performance', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: [
          ['Total Joins', analytics.waitlist_performance.total_joins.toString()],
          ['Avg Position When Offered', analytics.waitlist_performance.average_position_when_offered?.toFixed(1) || 'N/A'],
          ['Avg Time on Waitlist (days)', analytics.waitlist_performance.average_time_on_waitlist_days?.toFixed(1) || 'N/A']
        ],
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185] }
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yPos = (doc as any).lastAutoTable.finalY + 10;

      // Conversion Rates
      doc.setFontSize(14);
      doc.text('Conversion Rates', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value (%)']],
        body: [
          ['Notification Rate', analytics.conversion_funnel.notification_rate.toString()],
          ['Acceptance Rate', analytics.conversion_funnel.acceptance_rate.toString()],
          ['Overall Conversion Rate', analytics.conversion_funnel.overall_conversion_rate.toString()]
        ],
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185] }
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yPos = (doc as any).lastAutoTable.finalY + 10;

      // Status Breakdown
      doc.setFontSize(14);
      doc.text('Status Breakdown', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Status', 'Count']],
        body: Object.entries(analytics.waitlist_performance.status_breakdown).map(([status, count]) => [
          status,
          count.toString()
        ]),
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185] }
      });

      // Add new page if needed
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((doc as any).lastAutoTable.finalY > 250) {
        doc.addPage();
        yPos = 20;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        yPos = (doc as any).lastAutoTable.finalY + 10;
      }

      // Most Popular Fairs
      doc.setFontSize(14);
      doc.text('Most Popular Fairs', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Fair Name', 'Joins Count']],
        body: analytics.user_behavior.most_popular_fairs.map((fair) => [
          fair.fair_name,
          fair.joins_count.toString()
        ]),
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185] }
      });

      // Save the PDF
      doc.save(`waitlist-analytics-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('PDF export failed:', error);
      setExportError('Failed to export PDF. Please try again.');
    } finally {
      isExportingRef.current = false; // Reset ref guard
      setIsExporting(false); // Reset UI state
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only fetch on mount

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return <div>No data available</div>;
  }

  // Check if there's no waitlist data for the selected period
  const hasNoData = analytics.waitlist_performance.total_joins === 0;

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Prepare data for charts
  const statusData = Object.entries(analytics.waitlist_performance.status_breakdown).map(([status, count]) => ({
    name: status,
    value: count
  }));

  const funnelData = [
    { stage: 'Joined', count: analytics.conversion_funnel.total_joins },
    { stage: 'Notified', count: analytics.conversion_funnel.notified_count },
    { stage: 'Converted', count: analytics.conversion_funnel.converted_count }
  ];

  return (
    <div className="container mx-auto p-6">
      {/* Issue #825: Header with ARIA label */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" aria-label="Waitlist Analytics Dashboard">
          Waitlist Analytics Dashboard
        </h1>

        {/* Issue #824, #826: Action buttons */}
        <div className="flex gap-2">
          {/* Issue #824: Manual refresh button */}
          <button
            onClick={handleManualRefresh}
            disabled={refreshing || loading}
            className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Refresh analytics data"
            title="Refresh analytics data"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>

          {/* Issue #826: Export buttons */}
          {analytics && !loading && (
            <>
              <button
                onClick={handleExportCSV}
                disabled={refreshing || isExporting}
                className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Export data as CSV"
                title={
                  refreshing ? "Please wait until refresh completes" :
                  isExporting ? "Export in progress..." :
                  "Export data as CSV"
                }
              >
                <FileSpreadsheet className="w-4 h-4" aria-hidden="true" />
                CSV
              </button>
              <button
                onClick={handleExportPDF}
                disabled={refreshing || isExporting}
                className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Export data as PDF"
                title={
                  refreshing ? "Please wait until refresh completes" :
                  isExporting ? "Export in progress..." :
                  "Export data as PDF"
                }
              >
                <FileText className="w-4 h-4" aria-hidden="true" />
                PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* Issue #824: Last refreshed timestamp */}
      {lastRefreshed && (
        <div className="text-sm text-gray-500 mb-4" aria-live="polite">
          Last refreshed: {lastRefreshed.toLocaleString()}
        </div>
      )}

      {/* Issue #844: Inline export error message (does not hide dashboard) */}
      {exportError && (
        <div
          className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4 flex items-center justify-between"
          role="alert"
          aria-live="assertive"
        >
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{exportError}</span>
          </div>
          <button
            onClick={() => setExportError(null)}
            className="text-yellow-800 hover:text-yellow-900 font-semibold"
            aria-label="Dismiss export error"
          >
            ×
          </button>
        </div>
      )}

      {/* Date Filter - Issue #825: Enhanced with ARIA labels */}
      <div className="bg-white shadow-md rounded-lg p-6 mb-6" role="region" aria-label="Date range filter">
        <div className="flex gap-4 items-end">
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border rounded px-3 py-2"
              aria-label="Select start date for analytics"
            />
          </div>
          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border rounded px-3 py-2"
              aria-label="Select end date for analytics"
            />
          </div>
          <button
            onClick={() => fetchAnalytics(false)}
            disabled={refreshing || loading}
            className="bg-teal-600 text-white px-6 py-2 rounded hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Apply date range filter"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Empty State */}
      {hasNoData && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-8 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">No Waitlist Data</h2>
          <p>There are no waitlist entries for the selected date range. Try adjusting the date filters or check back later.</p>
        </div>
      )}

      {/* Real-time Stats Cards - Issue #825: Added ARIA labels */}
      {!hasNoData && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" role="region" aria-label="Real-time statistics">
        <div className="bg-white shadow-md rounded-lg p-6" role="article" aria-label="Active waitlist entries">
          <div className="text-sm text-gray-500">Active Waitlist</div>
          <div className="text-3xl font-bold" aria-label={`${analytics.real_time_stats.active_waitlist_entries} active waitlist entries`}>
            {analytics.real_time_stats.active_waitlist_entries}
          </div>
        </div>
        <div className="bg-white shadow-md rounded-lg p-6" role="article" aria-label="Pending slot offers">
          <div className="text-sm text-gray-500">Pending Offers</div>
          <div className="text-3xl font-bold" aria-label={`${analytics.real_time_stats.pending_slot_offers} pending slot offers`}>
            {analytics.real_time_stats.pending_slot_offers}
          </div>
        </div>
        <div className="bg-white shadow-md rounded-lg p-6" role="article" aria-label="Recent accepts in 24 hours">
          <div className="text-sm text-gray-500">Accepts (24h)</div>
          <div className="text-3xl font-bold text-green-600" aria-label={`${analytics.real_time_stats.recent_accepts_24h} accepts in the last 24 hours`}>
            {analytics.real_time_stats.recent_accepts_24h}
          </div>
        </div>
        <div className="bg-white shadow-md rounded-lg p-6" role="article" aria-label="Recent declines in 24 hours">
          <div className="text-sm text-gray-500">Declines (24h)</div>
          <div className="text-3xl font-bold text-red-600" aria-label={`${analytics.real_time_stats.recent_declines_24h} declines in the last 24 hours`}>
            {analytics.real_time_stats.recent_declines_24h}
          </div>
        </div>
      </div>
      )}

      {/* Performance Metrics - Issue #825: Added ARIA labels */}
      {!hasNoData && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white shadow-md rounded-lg p-6" role="region" aria-label="Waitlist performance metrics">
          <h2 className="text-xl font-bold mb-4">Waitlist Performance</h2>
          <div className="space-y-2">
            <div>Total Joins: <span className="font-bold" aria-label={`Total joins: ${analytics.waitlist_performance.total_joins}`}>{analytics.waitlist_performance.total_joins}</span></div>
            <div>Avg Position When Offered: <span className="font-bold" aria-label={`Average position when offered: ${analytics.waitlist_performance.average_position_when_offered?.toFixed(1) || 'N/A'}`}>{analytics.waitlist_performance.average_position_when_offered?.toFixed(1) || 'N/A'}</span></div>
            <div>Avg Time on Waitlist: <span className="font-bold" aria-label={`Average time on waitlist: ${analytics.waitlist_performance.average_time_on_waitlist_days?.toFixed(1) || 'N/A'} days`}>{analytics.waitlist_performance.average_time_on_waitlist_days?.toFixed(1) || 'N/A'} days</span></div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6" role="region" aria-label="Conversion rate metrics">
          <h2 className="text-xl font-bold mb-4">Conversion Rates</h2>
          <div className="space-y-2">
            <div>Notification Rate: <span className="font-bold" aria-label={`Notification rate: ${analytics.conversion_funnel.notification_rate} percent`}>{analytics.conversion_funnel.notification_rate}%</span></div>
            <div>Acceptance Rate: <span className="font-bold" aria-label={`Acceptance rate: ${analytics.conversion_funnel.acceptance_rate} percent`}>{analytics.conversion_funnel.acceptance_rate}%</span></div>
            <div>Overall Conversion: <span className="font-bold" aria-label={`Overall conversion rate: ${analytics.conversion_funnel.overall_conversion_rate} percent`}>{analytics.conversion_funnel.overall_conversion_rate}%</span></div>
          </div>
        </div>
      </div>
      )}

      {/* Status Breakdown Chart - Issue #825: Added ARIA labels */}
      {!hasNoData && (
      <div className="bg-white shadow-md rounded-lg p-6 mb-6" role="region" aria-label="Status breakdown chart">
        <h2 className="text-xl font-bold mb-4">Status Breakdown</h2>
        <div role="img" aria-label="Pie chart showing waitlist status distribution">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}

      {/* Conversion Funnel Chart - Issue #825: Added ARIA labels */}
      {!hasNoData && (
      <div className="bg-white shadow-md rounded-lg p-6 mb-6" role="region" aria-label="Conversion funnel chart">
        <h2 className="text-xl font-bold mb-4">Conversion Funnel</h2>
        <div role="img" aria-label="Bar chart showing conversion funnel stages from joined to converted">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={funnelData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="stage" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}

      {/* Popular Fairs - Issue #825: Added ARIA labels */}
      {!hasNoData && (
      <div className="bg-white shadow-md rounded-lg p-6" role="region" aria-label="Most popular fairs">
        <h2 className="text-xl font-bold mb-4">Most Popular Fairs (by Waitlist Joins)</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full" role="table" aria-label="Table of most popular fairs by waitlist joins">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fair</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joins</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.user_behavior.most_popular_fairs.map((fair) => (
                <tr key={fair.fair_id}>
                  <td className="px-6 py-4 whitespace-nowrap">{fair.fair_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap font-bold">{fair.joins_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminAnalyticsDashboard;
